import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  getSynchronizationItemById,
  IDocumentVersion,
  ISynchronizationItem,
  ISynchronizationItemMeta,
} from "utils/DexieDBManager";
import { useDocumentActionsStatus } from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import { DOCUMENT_VERSION_TYPE, RESOURCES_KEYS } from "utils/i18n/resources";
import FilterDocumentCards, { ORDER_VALUES } from "./FilterDocumentCards";
import FontAwesomeSvgIcon from "Layouts/Common/FontAwesomeSvgIcon";
import PdfDocumentCards from "PdfDocumentGrid/PdfDocumentCards";
import { GlobalSearchField } from "./GlobalSearchField";
import DocumentPdfSearch from "./DocumentPdfSearch";
import CategorySelector from "./CategorySelector";
import svgFonts from "Layouts/Common/fontAwesome";
import VersionSelector from "./VersionSelector";
import { debounce } from "utils/utils";
import { isPwa } from "utils/pwa";

import {
  sortDocuments,
  filterDocumentByTitle,
  filterDocumentVersionToDisplay,
  getIsAckRequiredDocumentsCount,
  getStarredIsAckRequiredDocumentsCount,
} from "./helpers";

import {
  styledScrollingZone,
  styledPdfListAction,
  styledPdfListWrapper,
  styledLoadMoreButton,
  styledPdfDocumentCards,
} from "./stylesPdfDocumentsList";

const defaultChoosenDocumentVersion = {
  documentVersions: [],
  starredDocumentsCount: 0,
  documentVersionType: DOCUMENT_VERSION_TYPE.APPLICABLES,
};

const defaultFilters = {
  new: false,
  ack: false,
  ascOrder: true,
};

type ChoosenDocumentVersionType = {
  documentVersionType: DOCUMENT_VERSION_TYPE;
  documentVersions: IDocumentVersion[];
};

interface IPdfDocumentsListProps {
  synchronizationItemsFromDb: ISynchronizationItemMeta[];
  synchronizationItemsFromServer: ISynchronizationItem[];
}

const PdfDocumentsList: React.FunctionComponent<IPdfDocumentsListProps> = ({
  synchronizationItemsFromDb,
  synchronizationItemsFromServer,
}) => {
  const [
    filteredSynchronizationItemsFromServer,
    setFilteredSynchronizationItemsFromServer,
  ] = React.useState<ISynchronizationItem[]>(synchronizationItemsFromServer);
  const [
    filteredSynchronizationItemsFromDB,
    setFilteredSynchronizationItemsFromDB,
  ] = React.useState<ISynchronizationItemMeta[]>(synchronizationItemsFromDb);
  const [choosenDocumentVersion, setChoosenDocumentVersion] =
    React.useState<ChoosenDocumentVersionType>(defaultChoosenDocumentVersion);
  const [choosenSynchronizationItem, setChoosenSynchronizationItem] =
    React.useState<ISynchronizationItem>();
  const [documentVersionsToDisplay, setDocumentVersionsToDisplay] =
    React.useState<IDocumentVersion[]>([]);
  const [filteredDocumentVersions, setFilteredDocumentVersions] =
    React.useState<IDocumentVersion[]>([]);
  const [filters, setFilters] = React.useState<{ [key: string]: boolean }>(
    defaultFilters
  );
  const [orderValue, setSortValue] = React.useState<ORDER_VALUES>(
    ORDER_VALUES.TITLE
  );
  const [filterMenuStatus, setFilterMenuStatus] = React.useState(false);
  const { dispatch, state } = useDocumentActionsStatus();
  const [page, setPage] = React.useState(1);
  const { t: translate } = useTranslation();
  const { pathname } = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isStarredPage = pathname === "/starred";
  const isCategoriesPage = pathname === "/categories";
  const isAcknowledgementPage = pathname === "/acknowledgement";
  const isOnline = navigator.onLine;

  const handleChangeSelect = (event: SelectChangeEvent) => {
    setSortValue(event.target.value as ORDER_VALUES);
  };

  const [globalSearchQuery, setGlobalSearchQuery] = useState<string>("");

  const loadMore = () => {
    const sourceDocumentVersions = filteredDocumentVersions.length
      ? filteredDocumentVersions
      : choosenDocumentVersion.documentVersions;

    const filteredVersions = getFiltredDocumentVersions(
      sourceDocumentVersions,
      pathname
    );

    const newDocumentVersionsToDisplay = filteredVersions.slice(
      page * 30,
      (page + 1) * 30
    );

    setDocumentVersionsToDisplay([
      ...documentVersionsToDisplay,
      ...newDocumentVersionsToDisplay,
    ]);
    setPage((page) => page + 1);
  };

  const handleSetChoosenSynchronizationItem = React.useCallback(
    (selectedSynchronizationItem: ISynchronizationItem) =>
      setChoosenSynchronizationItem(selectedSynchronizationItem),
    []
  );

  const handleSetChoosenDocumentVersion = React.useCallback(
    (selectedDocumentVersion: ChoosenDocumentVersionType) =>
      setChoosenDocumentVersion(selectedDocumentVersion),
    []
  );

  const handleSetDocumentVersionsToDisplay = React.useCallback(
    (documentVersionsToDisplay: IDocumentVersion[]) =>
      setDocumentVersionsToDisplay(documentVersionsToDisplay),
    []
  );

  const handleToggleFilter = React.useCallback(
    () => setFilterMenuStatus((prev) => !prev),
    []
  );

  const handleFiltersChanges = React.useCallback(
    (key: string, value: boolean) =>
      setFilters((filters) => ({ ...filters, [key]: value })),
    []
  );

  const debouncedSearchDocumentVersions = React.useMemo(
    () =>
      debounce((newDocTitle: string) => {
        if (newDocTitle) {
          let filteredDocumentVersions =
            choosenDocumentVersion.documentVersions.filter(
              filterDocumentByTitle(newDocTitle)
            );

          setDocumentVersionsToDisplay(filteredDocumentVersions.slice(0, 30));
          setFilteredDocumentVersions(filteredDocumentVersions);
        } else {
          setDocumentVersionsToDisplay(
            choosenDocumentVersion.documentVersions.slice(0, 30)
          );
          setFilteredDocumentVersions([]);
        }
      }, 1500),
    [choosenDocumentVersion]
  );

  const handleSearchChange = useCallback(
    ({ target: { value: query } }: React.ChangeEvent<HTMLInputElement>) =>
      setGlobalSearchQuery(query),
    []
  );

  const handlehandleOnReset = useCallback(() => {
    setGlobalSearchQuery("");
    setDocumentVersionsToDisplay([]);
    setChoosenSynchronizationItem(undefined);

    if (isPwa && !isOnline) {
      setFilteredSynchronizationItemsFromDB(synchronizationItemsFromDb);
      setChoosenDocumentVersion(defaultChoosenDocumentVersion);
      synchronizationItemsFromDb.forEach((synchronization) => {
        if (isCategoriesPage) {
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isCategoriesPageInitialized: true,
              synchronizationId: synchronization.id,
              documentsVersionCount:
                synchronization.categoriesDocumentVersionsCount,
              applicablesDocumentsCount:
                synchronization.categoriesApplicableDocumentsCount,
              nextApplicablesDocumentsCount:
                synchronization.categoriesNextApplicableDocumentsCount,
              isAckRequiredApplicableDocumentsCount:
                synchronization.categoriesIsAckRequiredApplicableDocumentsCount,
              isAckRequiredNextApplicableDocumentsCount:
                synchronization.categoriesIsAckRequiredNextApplicableDocumentsCount,
            },
          });
        } else if (isStarredPage) {
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isStarredPageInitialized: true,
              synchronizationId: synchronization.id,
              documentsVersionCount:
                synchronization.starredDocumentVersionsCount,
              applicablesDocumentsCount:
                synchronization.starredApplicableDocumentsCount,
              nextApplicablesDocumentsCount:
                synchronization.starredNextApplicableDocumentsCount,
              isAckRequiredApplicableDocumentsCount:
                synchronization.starredIsAckRequiredApplicableDocumentsCount,
              isAckRequiredNextApplicableDocumentsCount:
                synchronization.starredIsAckRequiredNextApplicableDocumentsCount,
            },
          });
        } else if (isAcknowledgementPage) {
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isAcknowledgementPageInitialized: true,
              synchronizationId: synchronization.id,
              documentsVersionCount:
                synchronization.acknowledgementDocumentVersionsCount,
              applicablesDocumentsCount:
                synchronization.acknowledgementApplicableDocumentsCount,
              nextApplicablesDocumentsCount:
                synchronization.acknowledgementNextApplicableDocumentsCount,
              isAckRequiredApplicableDocumentsCount:
                synchronization.acknowledgementIsAckRequiredApplicableDocumentsCount,
              isAckRequiredNextApplicableDocumentsCount:
                synchronization.acknowledgementIsAckRequiredNextApplicableDocumentsCount,
            },
          });
        }
      });
    } else {
      setFilteredSynchronizationItemsFromServer(synchronizationItemsFromServer);
      synchronizationItemsFromServer.forEach((synchronization) => {
        if (isCategoriesPage) {
          const applicablesCount = synchronization.applicables?.length ?? 0;
          const nextApplicablesCount =
            synchronization.nextApplicables?.length ?? 0;
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isCategoriesPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: applicablesCount,
              nextApplicablesDocumentsCount: nextApplicablesCount,
              documentsVersionCount: applicablesCount + nextApplicablesCount,
              isAckRequiredApplicableDocumentsCount:
                getIsAckRequiredDocumentsCount(synchronization.applicables),
              isAckRequiredNextApplicableDocumentsCount:
                getIsAckRequiredDocumentsCount(synchronization.nextApplicables),
            },
          });
        } else if (isStarredPage) {
          const starredApplicablesCount =
            synchronization.applicables?.length ?? 0;
          const starredNextApplicablesCount =
            synchronization.nextApplicables?.length ?? 0;
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isStarredPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: starredApplicablesCount,
              nextApplicablesDocumentsCount: starredNextApplicablesCount,
              documentsVersionCount:
                starredApplicablesCount + starredNextApplicablesCount,
              isAckRequiredApplicableDocumentsCount:
                getStarredIsAckRequiredDocumentsCount(
                  synchronization.applicables
                ),
              isAckRequiredNextApplicableDocumentsCount:
                getStarredIsAckRequiredDocumentsCount(
                  synchronization.nextApplicables
                ),
            },
          });
        } else if (isAcknowledgementPage) {
          const ackApplicablesCount = synchronization.applicables?.length ?? 0;
          const ackNextApplicablesCount =
            synchronization.nextApplicables?.length ?? 0;
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isAcknowledgementPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: ackApplicablesCount,
              nextApplicablesDocumentsCount: ackNextApplicablesCount,
              documentsVersionCount:
                ackApplicablesCount + ackNextApplicablesCount,
              isAckRequiredApplicableDocumentsCount:
                getIsAckRequiredDocumentsCount(synchronization.applicables),
              isAckRequiredNextApplicableDocumentsCount:
                getIsAckRequiredDocumentsCount(synchronization.nextApplicables),
            },
          });
        }
      });
    }
  }, [
    dispatch,
    isOnline,
    isStarredPage,
    isCategoriesPage,
    isAcknowledgementPage,
    synchronizationItemsFromDb,
    synchronizationItemsFromServer,
  ]);

  async function synchronizationItemById(documentVersionId: string) {
    return await getSynchronizationItemById(documentVersionId);
  }

  const handleOnClickSearch = useCallback(async () => {
    if (isPwa && !isOnline) {
      const results = await Promise.all(
        synchronizationItemsFromDb.map(
          async (item) =>
            (await synchronizationItemById(item.id)) as ISynchronizationItem
        )
      );
      const filteredSynchronizationItemsByGlobalSearch = results
        .filter((p) =>
          [...p.applicables, ...p.nextApplicables].some(
            filterDocumentByTitle(globalSearchQuery)
          )
        )
        .reduce((prev, curr) => {
          const filteredApplicableDocumentsByGlobalSearch =
            curr.applicables.filter(filterDocumentByTitle(globalSearchQuery));
          const filteredNextApplicableDocumentsByGlobalSearch =
            curr.nextApplicables.filter(
              filterDocumentByTitle(globalSearchQuery)
            );

          prev.push({
            ...curr,
            applicables: filteredApplicableDocumentsByGlobalSearch,
            nextApplicables: filteredNextApplicableDocumentsByGlobalSearch,
          });

          const filteredApplicableDocumentsByGlobalSearchLength =
            filteredApplicableDocumentsByGlobalSearch?.length ?? 0;
          const filteredNextApplicableDocumentsByGlobalSearchLength =
            filteredNextApplicableDocumentsByGlobalSearch?.length ?? 0;

          if (isCategoriesPage) {
            dispatch({
              type: "initCategoriesIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                synchronizationId: curr.id,
                applicablesDocumentsCount:
                  filteredApplicableDocumentsByGlobalSearchLength,
                nextApplicablesDocumentsCount:
                  filteredNextApplicableDocumentsByGlobalSearchLength,
                isAckRequiredApplicableDocumentsCount:
                  getIsAckRequiredDocumentsCount(
                    filteredApplicableDocumentsByGlobalSearch
                  ),
                isAckRequiredNextApplicableDocumentsCount:
                  getIsAckRequiredDocumentsCount(
                    filteredNextApplicableDocumentsByGlobalSearch
                  ),
                documentsVersionCount:
                  filteredApplicableDocumentsByGlobalSearchLength +
                  filteredNextApplicableDocumentsByGlobalSearchLength,
              },
            });
          } else if (isStarredPage) {
            dispatch({
              type: "initStarredIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                synchronizationId: curr.id,
                applicablesDocumentsCount:
                  filteredApplicableDocumentsByGlobalSearchLength,
                nextApplicablesDocumentsCount:
                  filteredNextApplicableDocumentsByGlobalSearchLength,
                documentsVersionCount:
                  filteredApplicableDocumentsByGlobalSearchLength +
                  filteredNextApplicableDocumentsByGlobalSearchLength,
                isAckRequiredApplicableDocumentsCount:
                  getStarredIsAckRequiredDocumentsCount(
                    filteredApplicableDocumentsByGlobalSearch
                  ),
                isAckRequiredNextApplicableDocumentsCount:
                  getStarredIsAckRequiredDocumentsCount(
                    filteredNextApplicableDocumentsByGlobalSearch
                  ),
              },
            });
          } else if (isAcknowledgementPage) {
            dispatch({
              type: "initAcknowledgementIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                synchronizationId: curr.id,
                applicablesDocumentsCount:
                  filteredApplicableDocumentsByGlobalSearchLength,
                nextApplicablesDocumentsCount:
                  filteredNextApplicableDocumentsByGlobalSearchLength,
                isAckRequiredApplicableDocumentsCount:
                  getIsAckRequiredDocumentsCount(
                    filteredApplicableDocumentsByGlobalSearch
                  ),
                isAckRequiredNextApplicableDocumentsCount:
                  getIsAckRequiredDocumentsCount(
                    filteredNextApplicableDocumentsByGlobalSearch
                  ),
                documentsVersionCount:
                  filteredApplicableDocumentsByGlobalSearchLength +
                  filteredNextApplicableDocumentsByGlobalSearchLength,
              },
            });
          }

          return prev;
        }, [] as ISynchronizationItem[])
        .sort((a, b) => a.name.localeCompare(b.name));

      const selectedSynchronizationItem =
        filteredSynchronizationItemsByGlobalSearch[0];
      if (selectedSynchronizationItem) {
        const synchronizationItem = (await getSynchronizationItemById(
          selectedSynchronizationItem?.id
        )) as ISynchronizationItem;

        const filteredApplicables = (
          synchronizationItem?.applicables ?? []
        ).filter(filterDocumentByTitle(globalSearchQuery));
        const filteredNextApplicables = (
          synchronizationItem?.nextApplicables ?? []
        ).filter(filterDocumentByTitle(globalSearchQuery));
        if (!filteredApplicables.length && filteredNextApplicables.length) {
          setDocumentVersionsToDisplay(
            selectedSynchronizationItem?.nextApplicables ?? []
          );
          setChoosenDocumentVersion({
            documentVersions: filteredNextApplicables,
            documentVersionType: DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES,
          });
        } else {
          setDocumentVersionsToDisplay(
            selectedSynchronizationItem?.applicables ?? []
          );
          setChoosenDocumentVersion({
            documentVersions: filteredApplicables,
            documentVersionType: DOCUMENT_VERSION_TYPE.APPLICABLES,
          });
        }

        const filteredSynchronizationItemsFromDB =
          synchronizationItemsFromDb.filter(
            (s) =>
              filteredSynchronizationItemsByGlobalSearch.findIndex(
                (f) => f.id === s.id
              ) > -1
          );
        setFilteredSynchronizationItemsFromDB(
          filteredSynchronizationItemsFromDB
        );
        setChoosenSynchronizationItem(synchronizationItem);
      } else {
        setFilteredDocumentVersions([]);
        setDocumentVersionsToDisplay([]);
        setChoosenSynchronizationItem(undefined);
        setFilteredSynchronizationItemsFromDB([]);
        setChoosenDocumentVersion({
          documentVersions: [],
          documentVersionType: DOCUMENT_VERSION_TYPE.APPLICABLES,
        });
      }
    } else {
      const filteredSynchronizationItemsByGlobalSearch =
        synchronizationItemsFromServer
          .filter((p) =>
            [...p.applicables, ...p.nextApplicables].some(
              filterDocumentByTitle(globalSearchQuery)
            )
          )
          .reduce((prev, curr) => {
            const filteredApplicableDocumentsByGlobalSearch =
              curr.applicables.filter(filterDocumentByTitle(globalSearchQuery));
            const filteredNextApplicableDocumentsByGlobalSearch =
              curr.nextApplicables.filter(
                filterDocumentByTitle(globalSearchQuery)
              );

            prev.push({
              ...curr,
              applicables: filteredApplicableDocumentsByGlobalSearch,
              nextApplicables: filteredNextApplicableDocumentsByGlobalSearch,
            });

            const filteredApplicableDocumentsByGlobalSearchLength =
              filteredApplicableDocumentsByGlobalSearch?.length ?? 0;
            const filteredNextApplicableDocumentsByGlobalSearchLength =
              filteredNextApplicableDocumentsByGlobalSearch?.length ?? 0;
            if (isCategoriesPage) {
              dispatch({
                type: "initCategoriesIsAckRequiredDocumentsState",
                isAckRequiredDocumentsState: {
                  synchronizationId: curr.id,
                  applicablesDocumentsCount:
                    filteredApplicableDocumentsByGlobalSearchLength,
                  nextApplicablesDocumentsCount:
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                  isAckRequiredApplicableDocumentsCount:
                    getIsAckRequiredDocumentsCount(
                      filteredApplicableDocumentsByGlobalSearch
                    ),
                  isAckRequiredNextApplicableDocumentsCount:
                    getIsAckRequiredDocumentsCount(
                      filteredNextApplicableDocumentsByGlobalSearch
                    ),
                  documentsVersionCount:
                    filteredApplicableDocumentsByGlobalSearchLength +
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                },
              });
            } else if (isStarredPage) {
              dispatch({
                type: "initStarredIsAckRequiredDocumentsState",
                isAckRequiredDocumentsState: {
                  synchronizationId: curr.id,
                  applicablesDocumentsCount:
                    filteredApplicableDocumentsByGlobalSearchLength,
                  nextApplicablesDocumentsCount:
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                  isAckRequiredApplicableDocumentsCount:
                    getStarredIsAckRequiredDocumentsCount(
                      filteredApplicableDocumentsByGlobalSearch
                    ),
                  isAckRequiredNextApplicableDocumentsCount:
                    getStarredIsAckRequiredDocumentsCount(
                      filteredNextApplicableDocumentsByGlobalSearch
                    ),
                  documentsVersionCount:
                    filteredApplicableDocumentsByGlobalSearchLength +
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                },
              });
            } else if (isAcknowledgementPage) {
              dispatch({
                type: "initAcknowledgementIsAckRequiredDocumentsState",
                isAckRequiredDocumentsState: {
                  synchronizationId: curr.id,
                  applicablesDocumentsCount:
                    filteredApplicableDocumentsByGlobalSearchLength,
                  nextApplicablesDocumentsCount:
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                  isAckRequiredApplicableDocumentsCount:
                    getIsAckRequiredDocumentsCount(
                      filteredApplicableDocumentsByGlobalSearch
                    ),
                  isAckRequiredNextApplicableDocumentsCount:
                    getIsAckRequiredDocumentsCount(
                      filteredNextApplicableDocumentsByGlobalSearch
                    ),
                  documentsVersionCount:
                    filteredApplicableDocumentsByGlobalSearchLength +
                    filteredNextApplicableDocumentsByGlobalSearchLength,
                },
              });
            }

            return prev;
          }, [] as ISynchronizationItem[])
          .sort((a, b) => a.name.localeCompare(b.name));

      setFilteredSynchronizationItemsFromServer(
        filteredSynchronizationItemsByGlobalSearch
      );

      const selectedSynchronizationItem =
        filteredSynchronizationItemsByGlobalSearch[0];
      setChoosenSynchronizationItem(selectedSynchronizationItem);

      const filteredApplicables = (
        selectedSynchronizationItem?.applicables ?? []
      ).filter(filterDocumentByTitle(globalSearchQuery));
      const filteredNextApplicables = (
        selectedSynchronizationItem?.nextApplicables ?? []
      ).filter(filterDocumentByTitle(globalSearchQuery));
      if (!filteredApplicables.length && filteredNextApplicables.length) {
        setDocumentVersionsToDisplay(filteredNextApplicables);
        setChoosenDocumentVersion({
          documentVersions: filteredNextApplicables,
          documentVersionType: DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES,
        });
      } else {
        setDocumentVersionsToDisplay(filteredApplicables);
        setChoosenDocumentVersion({
          documentVersions: filteredApplicables,
          documentVersionType: DOCUMENT_VERSION_TYPE.APPLICABLES,
        });
      }
    }
  }, [
    dispatch,
    isOnline,
    isStarredPage,
    isCategoriesPage,
    globalSearchQuery,
    isAcknowledgementPage,
    synchronizationItemsFromDb,
    synchronizationItemsFromServer,
  ]);

  const sortedDocumentVersionToDisplay = documentVersionsToDisplay.sort(
    sortDocuments(orderValue)
  );

  let documentVersionsToDisplayWithFilter: IDocumentVersion[] = [];
  if (!filters.new && !filters.ack) {
    documentVersionsToDisplayWithFilter = filters.ascOrder
      ? sortedDocumentVersionToDisplay
      : [...sortedDocumentVersionToDisplay].reverse();
  } else {
    const sortedDocumentVersionToDisplayWithFilter =
      sortedDocumentVersionToDisplay.filter(
        filterDocumentVersionToDisplay(filters, state)
      );
    documentVersionsToDisplayWithFilter = filters.ascOrder
      ? sortedDocumentVersionToDisplayWithFilter
      : [...sortedDocumentVersionToDisplayWithFilter].reverse();
  }

  return (
    <Box key={pathname}>
      <GlobalSearchField
        value={globalSearchQuery}
        onChange={handleSearchChange}
        onReset={handlehandleOnReset}
        onSearch={handleOnClickSearch}
        placeholder={translate(RESOURCES_KEYS.GLOBALSEARCH)}
      />
      <CategorySelector
        page={page}
        globalSearchQuery={globalSearchQuery}
        choosenSynchronizationItem={choosenSynchronizationItem}
        synchronizationItemsFromDb={filteredSynchronizationItemsFromDB}
        synchronizationItemsFromServer={filteredSynchronizationItemsFromServer}
        setChoosenSynchronizationItem={handleSetChoosenSynchronizationItem}
        setDocumentVersionsToDisplay={handleSetDocumentVersionsToDisplay}
        setChoosenDocumentVersion={handleSetChoosenDocumentVersion}
      />
      {choosenSynchronizationItem && (
        <>
          <Grid id="document-version-header" container spacing={2} pt={1}>
            {isMobile ? (
              <Grid item xs="auto">
                <Stack spacing={2} direction="row">
                  <DocumentPdfSearch
                    onSearchDocumentVersions={debouncedSearchDocumentVersions}
                  />
                  <Button
                    size="large"
                    color="inherit"
                    variant="outlined"
                    aria-label="Trier & filter"
                    onClick={handleToggleFilter}
                  >
                    <FontAwesomeSvgIcon icon={svgFonts.faFilter} />
                  </Button>
                </Stack>
              </Grid>
            ) : (
              <Grid item xs="auto">
                <DocumentPdfSearch
                  onSearchDocumentVersions={debouncedSearchDocumentVersions}
                />
              </Grid>
            )}
            <Grid
              id="versionSelector"
              item
              xs
              display="flex"
              justifyContent="center"
            >
              <VersionSelector
                choosenDocumentVersionType={
                  choosenDocumentVersion.documentVersionType
                }
                choosenSynchronizationItem={choosenSynchronizationItem}
                setChoosenDocumentVersion={handleSetChoosenDocumentVersion}
                setDocumentVersionsToDisplay={
                  handleSetDocumentVersionsToDisplay
                }
              />
            </Grid>
            {!isMobile && (
              <Grid item xs="auto">
                <Button
                  size="large"
                  color="inherit"
                  variant="outlined"
                  aria-label="Trier & filter"
                  onClick={handleToggleFilter}
                >
                  <FontAwesomeSvgIcon icon={svgFonts.faFilter} />
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid container sx={styledPdfListWrapper}>
            <Grid item xs sx={styledScrollingZone}>
              <Box id="pdfCardsList" sx={styledPdfDocumentCards}>
                <PdfDocumentCards
                  documentVersionsToDisplay={
                    documentVersionsToDisplayWithFilter
                  }
                />
              </Box>
              <Box sx={styledPdfListAction}>
                <Button
                  size="large"
                  color="inherit"
                  variant="outlined"
                  onClick={loadMore}
                  sx={styledLoadMoreButton}
                  // TODO: Rework this condition Elhoucine
                  disabled={
                    documentVersionsToDisplay.length ===
                      filteredDocumentVersions.length ||
                    documentVersionsToDisplay.length ===
                      choosenDocumentVersion.documentVersions.length
                  }
                >
                  {translate(RESOURCES_KEYS.LOAD_MORE)}
                </Button>
              </Box>
            </Grid>
            <Grid item xs="auto">
              <FilterDocumentCards
                filters={filters}
                orderValue={orderValue}
                filterMenuStatus={filterMenuStatus}
                onChangeSelect={handleChangeSelect}
                onFiltersChanges={handleFiltersChanges}
                onToggleFilterStatus={handleToggleFilter}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PdfDocumentsList;

const getFiltredDocumentVersions = (
  documentVersions: IDocumentVersion[],
  pathname: string
) => {
  const isStarredPage = pathname === "/starred";
  const isAcknowledgementPage = pathname === "/acknowledgement";

  if (isStarredPage) {
    return documentVersions.filter((item) => item.uds.isFav);
  }

  if (isAcknowledgementPage) {
    return documentVersions.filter((item) => item.uds.isAck);
  }

  return documentVersions;
};
